import * as React from 'react';

import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

type BaseProps<T> = Omit<
  AutocompleteProps<T, true, false, false>,
  | 'multiple'
  | 'renderInput'
  | 'disableCloseOnSelect'
  | 'onChange'
  | 'value'
  | 'options'
> & {
  label?: React.ReactNode;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'label' | 'placeholder' | 'helperText' | 'error'>;
  withCheckboxes?: boolean;
  options: T[];
  value: T[];
  onChange: (
    event: React.SyntheticEvent,
    value: T[],
    reason: 'createOption' | 'selectOption' | 'removeOption' | 'clear' | 'blur' | 'setOption',
    details?: any
  ) => void;
  getOptionLabel?: (option: T) => string;
  getTagLabel?: (option: T) => string;
  getOptionSecondaryLabel?: (option: T) => string | undefined;
};

function defaultGetOptionLabel<T>(option: T): string {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  if (typeof option === 'number') return String(option);
  if (typeof option === 'object') {
    // Best-effort defaults commonly used in the app
    // name_en (preferred) > name > label > id
    const anyOption = option as Record<string, unknown>;
    if (typeof anyOption.name_en === 'string' && anyOption.name_en) return anyOption.name_en as string;
    if (typeof anyOption.name === 'string' && anyOption.name) return anyOption.name as string;
    if (typeof anyOption.label === 'string' && anyOption.label) return anyOption.label as string;
    if (typeof anyOption.id === 'string' || typeof anyOption.id === 'number') return String(anyOption.id);
  }
  return '';
}

function defaultIsOptionEqualToValue<T>(option: T, value: T): boolean {
  if (option === value) return true;
  const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
  if (isObject(option) && isObject(value)) {
    const opt = option as Record<string, unknown>;
    const val = value as Record<string, unknown>;
    if ((opt.id != null) && (val.id != null)) return opt.id === val.id;
  }
  return false;
}

export type CustomMultiSelectProps<T> = BaseProps<T>;

export default function CustomMultiSelect<T>(props: BaseProps<T>) {
  const {
    label,
    placeholder,
    helperText,
    error,
    textFieldProps,
    withCheckboxes = true,
    options,
    value,
    onChange,
    getOptionLabel = defaultGetOptionLabel,
    getTagLabel,
    getOptionSecondaryLabel,
    isOptionEqualToValue,
    loading,
    renderTags,
    renderOption,
    ...other
  } = props as any;

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      blurOnSelect={false}
      options={options}
      value={value}
      onChange={onChange as any}
      getOptionLabel={getOptionLabel as any}
      isOptionEqualToValue={(isOptionEqualToValue as any) || (defaultIsOptionEqualToValue as any)}
      clearOnBlur={false}
      openOnFocus
      autoHighlight
      loading={loading}
      renderTags={
        renderTags ||
        ((tagValue: T[], getTagProps) =>
          tagValue.map((option: T, index: number) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={(getTagLabel ? getTagLabel(option) : getOptionLabel(option)) as any}
            />
          )))
      }
      renderOption={
        renderOption ||
        ((liProps, option: T, { selected }) => {
          const primary = getOptionLabel(option);
          const secondary = getOptionSecondaryLabel?.(option);
          return (
            <Box
              component="li"
              {...liProps}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                px: 1,
                borderRadius: 1,
                ...(selected && {
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                  '& .MuiListItemText-primary': { fontWeight: 600 },
                }),
              })}
            >
              {withCheckboxes && (
                <Checkbox sx={{ mr: 1 }} checked={selected} tabIndex={-1} disableRipple />
              )}
              <ListItemText
                primary={primary}
                secondary={secondary}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </Box>
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
      {...other}
    />
  );
}


